import type { TournamentDetail } from "../../types/swagger";
import { mockMemberLoginIds, mockMemberNames } from "./members";

const getMemberName = (memberId: string): string =>
  mockMemberNames[memberId] ?? "BYE";

const getLoginId = (memberId: string): string =>
  mockMemberLoginIds[memberId] ?? "BYE";

const toBestOfThreeWins = (wins: number): number => (wins === 1 ? 2 : wins);

const STAGE999_EVENT_ID = "fd7909b0-c0fc-44f8-816e-77d20e5a3ddf";
const STAGE888_EVENT_ID = "1f2e3d4c-5b6a-7980-1234-abcdef987654";

const createDetail = (
  eventId: string,
  tournamentId: string,
  memberId: string,
  round: number,
  cellNumber: number,
  blockNumber: number,
  wins: number,
): TournamentDetail => ({
  tournament_id: tournamentId,
  event_id: eventId,
  member_id: memberId,
  login_id: getLoginId(memberId),
  round,
  cell_number: cellNumber,
  block_number: blockNumber,
  member_name: getMemberName(memberId),
  wins: toBestOfThreeWins(wins),
});

/** Stage999 用: シングルス トーナメント詳細（16人想定） */
const stage999TournamentDetails: TournamentDetail[] = [
  // round=16: best 16
  createDetail(STAGE999_EVENT_ID, "1", "1", 16, 1, 1, 1),
  createDetail(STAGE999_EVENT_ID, "2", "2", 16, 2, 1, 0),
  createDetail(STAGE999_EVENT_ID, "3", "3", 16, 1, 2, 1),
  createDetail(STAGE999_EVENT_ID, "4", "4", 16, 2, 2, 0),
  createDetail(STAGE999_EVENT_ID, "5", "5", 16, 1, 3, 0),
  createDetail(STAGE999_EVENT_ID, "6", "6", 16, 2, 3, 1),
  createDetail(STAGE999_EVENT_ID, "7", "7", 16, 1, 4, 1),
  createDetail(STAGE999_EVENT_ID, "8", "8", 16, 2, 4, 0),
  createDetail(STAGE999_EVENT_ID, "9", "9", 16, 1, 5, 1),
  createDetail(STAGE999_EVENT_ID, "10", "10", 16, 2, 5, 0),
  createDetail(STAGE999_EVENT_ID, "11", "11", 16, 1, 6, 0),
  createDetail(STAGE999_EVENT_ID, "12", "12", 16, 2, 6, 1),
  createDetail(STAGE999_EVENT_ID, "13", "13", 16, 1, 7, 1),
  createDetail(STAGE999_EVENT_ID, "14", "14", 16, 2, 7, 0),
  createDetail(STAGE999_EVENT_ID, "15", "15", 16, 1, 8, 1),
  createDetail(STAGE999_EVENT_ID, "16", "16", 16, 2, 8, 0),

  // round=8: quarterfinal (winners from round16)
  createDetail(STAGE999_EVENT_ID, "17", "1", 8, 1, 1, 1),
  createDetail(STAGE999_EVENT_ID, "18", "3", 8, 2, 1, 0),
  createDetail(STAGE999_EVENT_ID, "19", "6", 8, 1, 2, 0),
  createDetail(STAGE999_EVENT_ID, "20", "7", 8, 2, 2, 1),
  createDetail(STAGE999_EVENT_ID, "21", "9", 8, 1, 3, 0),
  createDetail(STAGE999_EVENT_ID, "22", "12", 8, 2, 3, 1),
  createDetail(STAGE999_EVENT_ID, "23", "13", 8, 1, 4, 1),
  createDetail(STAGE999_EVENT_ID, "24", "15", 8, 2, 4, 0),

  // round=4: semifinal (winners from round8)
  createDetail(STAGE999_EVENT_ID, "25", "1", 4, 1, 1, 1),
  createDetail(STAGE999_EVENT_ID, "26", "7", 4, 2, 1, 0),
  createDetail(STAGE999_EVENT_ID, "27", "12", 4, 1, 2, 0),
  createDetail(STAGE999_EVENT_ID, "28", "13", 4, 2, 2, 1),

  // round=2: final (winners from semifinal)
  createDetail(STAGE999_EVENT_ID, "29", "1", 2, 1, 1, 0),
  createDetail(STAGE999_EVENT_ID, "30", "13", 2, 2, 1, 1),
];

/** Stage888 用: シングルス トーナメント詳細（15人想定。1枠はBYE） */
const stage888TournamentDetails: TournamentDetail[] = [
  // round=16: best 16 (block 8 is single player -> BYE)
  createDetail(STAGE888_EVENT_ID, "101", "1", 16, 1, 1, 1),
  createDetail(STAGE888_EVENT_ID, "102", "2", 16, 2, 1, 0),
  createDetail(STAGE888_EVENT_ID, "103", "3", 16, 1, 2, 1),
  createDetail(STAGE888_EVENT_ID, "104", "4", 16, 2, 2, 0),
  createDetail(STAGE888_EVENT_ID, "105", "5", 16, 1, 3, 1),
  createDetail(STAGE888_EVENT_ID, "106", "6", 16, 2, 3, 0),
  createDetail(STAGE888_EVENT_ID, "107", "7", 16, 1, 4, 1),
  createDetail(STAGE888_EVENT_ID, "108", "8", 16, 2, 4, 0),
  createDetail(STAGE888_EVENT_ID, "109", "9", 16, 1, 5, 1),
  createDetail(STAGE888_EVENT_ID, "110", "10", 16, 2, 5, 0),
  createDetail(STAGE888_EVENT_ID, "111", "11", 16, 1, 6, 0),
  createDetail(STAGE888_EVENT_ID, "112", "12", 16, 2, 6, 1),
  createDetail(STAGE888_EVENT_ID, "113", "13", 16, 1, 7, 1),
  createDetail(STAGE888_EVENT_ID, "114", "14", 16, 2, 7, 0),
  createDetail(STAGE888_EVENT_ID, "115", "15", 16, 1, 8, 1),

  // round=8: quarterfinal (winners from round16)
  createDetail(STAGE888_EVENT_ID, "116", "1", 8, 1, 1, 1),
  createDetail(STAGE888_EVENT_ID, "117", "3", 8, 2, 1, 0),
  createDetail(STAGE888_EVENT_ID, "118", "5", 8, 1, 2, 1),
  createDetail(STAGE888_EVENT_ID, "119", "7", 8, 2, 2, 0),
  createDetail(STAGE888_EVENT_ID, "120", "9", 8, 1, 3, 0),
  createDetail(STAGE888_EVENT_ID, "121", "12", 8, 2, 3, 1),
  createDetail(STAGE888_EVENT_ID, "122", "13", 8, 1, 4, 1),
  createDetail(STAGE888_EVENT_ID, "123", "15", 8, 2, 4, 0),

  // round=4: semifinal (winners from round8)
  createDetail(STAGE888_EVENT_ID, "124", "1", 4, 1, 1, 0),
  createDetail(STAGE888_EVENT_ID, "125", "5", 4, 2, 1, 1),
  createDetail(STAGE888_EVENT_ID, "126", "12", 4, 1, 2, 1),
  createDetail(STAGE888_EVENT_ID, "127", "13", 4, 2, 2, 0),

  // round=2: final (winners from semifinal)
  createDetail(STAGE888_EVENT_ID, "128", "5", 2, 1, 1, 1),
  createDetail(STAGE888_EVENT_ID, "129", "12", 2, 2, 1, 0),
];

const PARTIAL_TEST_EVENT_ID = "c0d1e2f3-a4b5-6789-cdef-012345678901";

/**
 * 中間状態テスト用:
 * round=16 の block=8 だけ先に結果登録した状態を模擬。
 * round=16 全8ブロックは完了済み、round=8 は block=4 のみ存在する。
 * 修正前: block=4 が1番上（index 0）に描画される。
 * 修正後: block=4 が正しく4番目のスロットに描画される。
 */
const partialTestTournamentDetails: TournamentDetail[] = [
  // round=16: 全8ブロック結果登録済み
  createDetail(PARTIAL_TEST_EVENT_ID, "201", "1",  16, 1, 1, 2),
  createDetail(PARTIAL_TEST_EVENT_ID, "202", "2",  16, 2, 1, 0),
  createDetail(PARTIAL_TEST_EVENT_ID, "203", "3",  16, 1, 2, 2),
  createDetail(PARTIAL_TEST_EVENT_ID, "204", "4",  16, 2, 2, 0),
  createDetail(PARTIAL_TEST_EVENT_ID, "205", "5",  16, 1, 3, 2),
  createDetail(PARTIAL_TEST_EVENT_ID, "206", "6",  16, 2, 3, 0),
  createDetail(PARTIAL_TEST_EVENT_ID, "207", "7",  16, 1, 4, 2),
  createDetail(PARTIAL_TEST_EVENT_ID, "208", "8",  16, 2, 4, 0),
  createDetail(PARTIAL_TEST_EVENT_ID, "209", "9",  16, 1, 5, 2),
  createDetail(PARTIAL_TEST_EVENT_ID, "210", "10", 16, 2, 5, 0),
  createDetail(PARTIAL_TEST_EVENT_ID, "211", "11", 16, 1, 6, 2),
  createDetail(PARTIAL_TEST_EVENT_ID, "212", "12", 16, 2, 6, 0),
  createDetail(PARTIAL_TEST_EVENT_ID, "213", "13", 16, 1, 7, 2),
  createDetail(PARTIAL_TEST_EVENT_ID, "214", "14", 16, 2, 7, 0),
  createDetail(PARTIAL_TEST_EVENT_ID, "215", "15", 16, 1, 8, 2),
  createDetail(PARTIAL_TEST_EVENT_ID, "216", "16", 16, 2, 8, 0),

  // round=8: block=8(round=16) が最初に登録されたため block=4 のみ存在
  createDetail(PARTIAL_TEST_EVENT_ID, "217", "15", 8, 1, 4, 0),
  createDetail(PARTIAL_TEST_EVENT_ID, "218", "16", 8, 2, 4, 0),
];

export const mockTournamentDetails: TournamentDetail[] = [
  ...stage999TournamentDetails,
  ...stage888TournamentDetails,
  ...partialTestTournamentDetails,
];
